import type {FC} from 'react'
import type React from 'react'
import {useRef, useState, useEffect} from 'react'
import {FormControl, Link, TextInput} from '@primer/react'
import {PlusCircleIcon, XCircleIcon} from '@primer/octicons-react'
import {Dialog} from '@primer/react/experimental'
import {EXAMPLE_CONDITION_TARGET_PATTERNS, PLURAL_RULESET_TARGETS} from '../../../helpers/constants'
import type {RulesetTarget, TargetType} from '@github-ui/repos-rules-types/rules-types'
import {capitalize} from '../../../helpers/string'
import {useRelativeNavigation} from '../../../hooks/use-relative-navigation'
import {useValidateConditionTarget} from '../../../hooks/use-validate-condition-target'
import {useRuleStrings} from '../../../hooks/use-rule-strings'

import styles from './AddTargetDialog.module.css'

// Copy overrides for target types whose subject is not the ruleset target itself.
const TARGET_COPY: Partial<Record<TargetType, {plural: string; patternLabel: string}>> = {
  repository_name: {plural: 'Repositories', patternLabel: 'Repository naming pattern'},
  organization_name: {plural: 'Organizations', patternLabel: 'Organization naming pattern'},
  workflow_path: {plural: 'Workflows', patternLabel: 'Workflow path pattern'},
}

type AddTargetDialogProps = {
  rulesetId?: number
  onAdd: (target: string) => void
  onClose: () => void
  target: TargetType
  includeOrExclude: 'include' | 'exclude'
  rulesetTarget: RulesetTarget
  fnmatchHelpUrl?: string
  returnFocusRef?: React.RefObject<HTMLButtonElement | null>
}

export const AddTargetDialog: FC<AddTargetDialogProps> = ({
  rulesetId,
  onAdd,
  onClose,
  target,
  includeOrExclude,
  rulesetTarget,
  fnmatchHelpUrl,
  returnFocusRef,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const {rulesetOrPolicy} = useRuleStrings()

  const examplePatterns = EXAMPLE_CONDITION_TARGET_PATTERNS[target]?.[rulesetTarget]

  const title = `${capitalize(includeOrExclude)} by pattern`
  const targetCopy = TARGET_COPY[target]
  const subtitleObject = capitalize(targetCopy?.plural ?? PLURAL_RULESET_TARGETS[rulesetTarget])
  const subtitle = `${subtitleObject} that ${
    includeOrExclude === 'include' ? 'match' : 'do not match'
  } the matching pattern will be targeted by this ${rulesetOrPolicy}.`
  const patternLabel = targetCopy?.patternLabel ?? `${capitalize(rulesetTarget)} naming pattern`
  const createText = `Add ${includeOrExclude === 'include' ? 'Inclusion' : 'Exclusion'} pattern`

  const [conditionTargetError, setConditionTargetError] = useState('')
  const {resolvePath} = useRelativeNavigation()
  const conditionTargetValidator = useValidateConditionTarget(resolvePath('../validate_value'))

  const onSave = async () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const trimmedParam = inputRef.current!.value.trim()

    if (!trimmedParam || trimmedParam === '') {
      setConditionTargetError('Pattern cannot be empty')
      inputRef.current?.focus()
      return
    }
    if (!conditionTargetValidator.isValid) {
      return
    }

    onAdd(trimmedParam)
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    inputRef.current!.value = ''
  }

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    onSave()
  }

  // For reasons unknown, calling .focus() causes us to focus, then blur, then focus the text input. The spurious call
  // to onBlur() causes us to display an unwarranted error messsage. Waiting until the next event loop avoids this.
  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus()
    })

    return () => {
      window.clearTimeout(timeout)
    }
  }, [])

  return (
    <Dialog
      aria-label={title}
      title={title}
      subtitle={<span className="text-normal text-small color-fg-muted">{subtitle}</span>}
      footerButtons={[
        {content: 'Cancel', buttonType: 'normal', onClick: onClose},
        {content: createText, onClick: onSave, buttonType: 'primary'},
      ]}
      onClose={onClose}
      width="medium"
      returnFocusRef={returnFocusRef}
    >
      <FormControl className={styles.AddTargetDialogFormControl}>
        <FormControl.Label>
          <span className="text-bold">{patternLabel}</span>
        </FormControl.Label>
        <TextInput
          leadingVisual={() =>
            includeOrExclude === 'include' ? (
              <PlusCircleIcon className={styles.AddTargetDialogIncludeIcon} />
            ) : (
              <XCircleIcon className={styles.AddTargetDialogExcludeIcon} />
            )
          }
          ref={inputRef}
          className="width-full"
          onKeyPress={onKeyPress}
          onBlur={e => {
            const relatedTarget = e.relatedTarget as HTMLElement | null

            if (
              !relatedTarget ||
              relatedTarget.closest('a[href]') ||
              relatedTarget.textContent === 'Cancel' ||
              relatedTarget.textContent === ''
            ) {
              return
            }

            if (!e.target.value || e.target.value.trim() === '') {
              setConditionTargetError('Pattern cannot be empty')
              inputRef.current?.focus()
              return
            }

            setConditionTargetError('')
          }}
          onChange={e => {
            setConditionTargetError?.('')
            conditionTargetValidator.validate(e.target.value.trim(), target, rulesetId)
          }}
        />
        {(examplePatterns?.length || 0) > 0 && (
          <FormControl.Caption>
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            Example patterns: &quot;{examplePatterns!.join('", "')}&quot;.
            {fnmatchHelpUrl && (
              <>
                &nbsp;
                <Link inline target="_blank" href={fnmatchHelpUrl}>
                  Learn more about fnmatch
                </Link>
                .
              </>
            )}
          </FormControl.Caption>
        )}
        {conditionTargetValidator.showError && (
          <FormControl.Validation variant="error" aria-live="polite">
            Invalid pattern
          </FormControl.Validation>
        )}
        {conditionTargetError && (
          <FormControl.Validation variant="error">{conditionTargetError}</FormControl.Validation>
        )}
      </FormControl>
    </Dialog>
  )
}
